import React, { useState, useEffect } from "react";
import { useToast, useFormState } from '../utils';
import APGARScoringDialog from './APGARScoringDialog';

BirthEventForm.defaultProps = {
  onSave: () => {},
  saveStatus: "saved",
};

const TabButton = ({ isActive, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium border-b-2 ${
      isActive
        ? "border-emerald-600 text-emerald-700"
        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
    }`}
  >
    {children}
  </button>
);

const TimeInput = ({ label, startName, endName, value, onChange, required }) => (
  <div>
    <label className="block mb-1">{label}{required && '*'}</label>
    <div className="grid grid-cols-2 gap-2">
      <div>
        <input
          type="datetime-local"
          name={startName}
          value={value[startName] || ""}
          onChange={onChange}
          required={required}
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full text-sm"
        />
        <span className="text-xs text-gray-500">Start</span>
      </div>
      <div>
        <input
          type="datetime-local"
          name={endName}
          value={value[endName] || ""}
          onChange={onChange}
          required={required}
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full text-sm"
        />
        <span className="text-xs text-gray-500">End</span>
      </div>
    </div>
  </div>
);

const TimeRecordButton = ({ label, time, fieldName, onChange }) => {
  const recordTime = () => {
    const now = new Date();
    const timeString = now.toLocaleTimeString('en-US', { hour12: false });
    onChange({
      target: {
        name: fieldName,
        value: timeString
      }
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="block mb-1">{label}*</label>
      <div className="flex gap-2 items-center">
        <input
          type="time"
          name={fieldName}
          value={time || ""}
          onChange={onChange}
          required
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 flex-1"
        />
        <button
          type="button"
          onClick={recordTime}
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 rounded text-sm whitespace-nowrap"
        >
          Record Time
        </button>
      </div>
    </div>
  );
};

const LaborStageRecorder = ({ label, startName, value, onChange, previousStageStart, required }) => {
  const recordTime = () => {
    const now = new Date();
    // Adjust for local timezone
    const localDate = new Date(now.getTime() - (now.getTimezoneOffset() * 60000));
    const timeString = localDate.toISOString().slice(0, 16); // Format: YYYY-MM-DDThh:mm
    onChange({
      target: {
        name: startName,
        value: timeString
      }
    });
  };

  const calculateDuration = () => {
    if (!value[startName]) return null;
    
    // Find the end time (either the next stage's start or current time)
    const nextStageStart = getNextStageStart(startName, value);
    const endTime = nextStageStart ? new Date(nextStageStart) : new Date();
    const startTime = new Date(value[startName]);
    
    // Ensure we're comparing in the same timezone
    const startTimeLocal = new Date(startTime.getTime() - (startTime.getTimezoneOffset() * 60000));
    const endTimeLocal = new Date(endTime.getTime() - (endTime.getTimezoneOffset() * 60000));
    
    const diffMinutes = Math.floor((endTimeLocal - startTimeLocal) / (1000 * 60));
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    
    return { hours, minutes };
  };

  const duration = calculateDuration();
  const isDisabled = previousStageStart ? !value[previousStageStart] : false;

  return (
    <div className="border rounded-lg p-4 bg-white">
      <label className="block mb-2 font-medium">{label}{required && '*'}</label>
      <div className="space-y-2">
        <div className="flex gap-2 items-center">
          <input
            type="datetime-local"
            name={startName}
            value={value[startName] || ""}
            onChange={onChange}
            required={required}
            className="input shadow-sm border border-gray-300 rounded px-2 py-1 flex-1 text-sm"
          />
          <button
            type="button"
            onClick={recordTime}
            disabled={isDisabled}
            className={`px-3 py-1 rounded text-sm whitespace-nowrap ${
              isDisabled
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-emerald-600 hover:bg-emerald-700 text-white"
            }`}
          >
            Record Start
          </button>
        </div>
        {duration && (
          <div className="text-sm text-gray-600">
            Duration: {duration.hours}h {duration.minutes}m
            {!getNextStageStart(startName, value) && " (ongoing)"}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to find the next stage's start time
const getNextStageStart = (currentStageName, value) => {
  const stageOrder = [
    'earlyLaborStart',
    'activeLaborStart',
    'secondStageStart',
    'thirdStageStart',
    'placentaDeliveryTime'
  ];
  
  const currentIndex = stageOrder.indexOf(currentStageName);
  if (currentIndex === -1 || currentIndex === stageOrder.length - 1) return null;
  
  return value[stageOrder[currentIndex + 1]];
};

// Calculate total duration of all stages
const calculateTotalDuration = (value) => {
  if (!value.earlyLaborStart) return null;

  const startTime = new Date(value.earlyLaborStart);
  const endTime = value.placentaDeliveryTime ? new Date(value.placentaDeliveryTime) : new Date();

  // Ensure we're comparing in the same timezone
  const startTimeLocal = new Date(startTime.getTime() - (startTime.getTimezoneOffset() * 60000));
  const endTimeLocal = new Date(endTime.getTime() - (endTime.getTimezoneOffset() * 60000));
  
  const diffMinutes = Math.floor((endTimeLocal - startTimeLocal) / (1000 * 60));
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  
  return { hours, minutes };
};

const RadioGroup = ({ label, name, value, onChange }) => (
  <div>
    <label className="block mb-1">{label}</label>
    <div className="flex gap-4">
      <label className="flex items-center">
        <input
          type="radio"
          name={name}
          value="Yes"
          checked={value === "Yes"}
          onChange={onChange}
          className="mr-2"
        />
        Yes
      </label>
      <label className="flex items-center">
        <input
          type="radio"
          name={name}
          value="No"
          checked={value === "No"}
          onChange={onChange}
          className="mr-2"
        />
        No
      </label>
    </div>
  </div>
);

const calculateTimeDifference = (startTime, endTime) => {
  if (!startTime || !endTime) return null;
  
  const start = new Date(`1970-01-01T${startTime}`);
  const end = new Date(`1970-01-01T${endTime}`);
  const diffMs = end - start;
  
  const minutes = Math.floor(diffMs / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);
  
  return `${minutes} min ${seconds} sec`;
};

export default function BirthEventForm({ baby, onChange, onSave, saveStatus }) {
  const { showToast } = useToast();
  const { form: weightUnit, handleChange: handleWeightUnitChange } = useFormState({ unit: "US" });
  const [activeTab, setActiveTab] = useState("basic");
  const [showStatus, setShowStatus] = useState(false);
  const [showAPGARDialog, setShowAPGARDialog] = useState(false);
  const [currentAPGARTimepoint, setCurrentAPGARTimepoint] = useState(null);
  const [currentNote, setCurrentNote] = useState("");

  // Handle status message visibility
  useEffect(() => {
    if (saveStatus === "saved" || saveStatus === "error") {
      setShowStatus(true);
      const timer = setTimeout(() => {
        setShowStatus(false);
      }, 3000); // Hide after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [saveStatus]);

  // Validation helpers
  const validateWeight = () => {
    if (weightUnit.unit === "US") {
      const lbs = parseFloat(baby.weightLbs || 0);
      const oz = parseFloat(baby.weightOz || 0);
      return lbs > 0 || oz > 0;
    }
    return parseFloat(baby.weightKg || 0) > 0;
  };

  const validateForm = () => {
    const requiredFields = ['firstName', 'lastName', 'dateOfBirth', 'timeOfBirth', 'sex'];
    const missingFields = requiredFields.filter(field => !baby[field]);
    
    if (missingFields.length > 0) {
      showToast(`Please fill in: ${missingFields.join(', ')}`, 5000);
      return false;
    }
    
    if (!validateWeight()) {
      showToast('Please enter a valid birth weight', 5000);
      return false;
    }
    
    return true;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    // Convert weight to kg for storage if in US units
    const finalData = { ...baby };
    if (weightUnit.unit === "US") {
      const lbs = parseFloat(baby.weightLbs || 0);
      const oz = parseFloat(baby.weightOz || 0);
      finalData.weightKg = (lbs * 0.45359237) + (oz * 0.028349523125);
    }
    
    await onSave(finalData);
  };

  const handleOpenAPGARDialog = (timepoint) => {
    setCurrentAPGARTimepoint(timepoint);
    setShowAPGARDialog(true);
  };

  const handleAPGARScoreSubmit = (score, timepoint) => {
    onChange({
      target: {
        name: `apgar${timepoint}Min`,
        value: score
      }
    });
  };

  const handleAddNote = () => {
    if (!currentNote.trim()) return;
    
    const newNote = {
      timestamp: new Date().toISOString(),
      content: currentNote.trim()
    };
    
    const updatedNotes = [...(baby.laborNotes || []), newNote];
    onChange({
      target: {
        name: "laborNotes",
        value: updatedNotes
      }
    });
    setCurrentNote("");
  };

  const renderBasicInfo = () => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div>
        <label className="block mb-1">First Name*</label>
        <input
          name="firstName"
          value={baby.firstName || ""}
          onChange={onChange}
          required
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
        />
      </div>
      <div>
        <label className="block mb-1">Middle Name</label>
        <input
          name="middleName"
          value={baby.middleName || ""}
          onChange={onChange}
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
        />
      </div>
      <div>
        <label className="block mb-1">Last Name*</label>
        <input
          name="lastName"
          value={baby.lastName || ""}
          onChange={onChange}
          required
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
        />
      </div>

      <div>
        <label className="block mb-1">Date of Birth*</label>
        <input
          type="date"
          name="dateOfBirth"
          value={baby.dateOfBirth || ""}
          onChange={onChange}
          required
          max={new Date().toISOString().split('T')[0]}
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
        />
      </div>
      <div>
        <label className="block mb-1">Time of Birth*</label>
        <input
          type="time"
          name="timeOfBirth"
          value={baby.timeOfBirth || ""}
          onChange={onChange}
          required
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
        />
      </div>
      <div>
        <label className="block mb-1">Sex*</label>
        <select
          name="sex"
          value={baby.sex || ""}
          onChange={onChange}
          required
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
        >
          <option value="">Select</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </select>
      </div>

      {/* Twin-specific fields */}
      {baby.isTwin && (
        <>
          <div>
            <label className="block mb-1">Twin Order*</label>
            <div className="px-3 py-2 bg-gray-100 rounded text-gray-700">
              Twin {baby.twinOrder}
            </div>
          </div>
          <div>
            <label className="block mb-1">Twin Type</label>
            <select
              name="twinType"
              value={baby.twinType || ""}
              onChange={onChange}
              className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
            >
              <option value="">Select Twin Type</option>
              <option value="Identical">Identical (Monozygotic)</option>
              <option value="Fraternal">Fraternal (Dizygotic)</option>
              <option value="Unknown">Unknown</option>
            </select>
          </div>
          <div>
            <label className="block mb-1">Chorionicity</label>
            <select
              name="chorionicity"
              value={baby.chorionicity || ""}
              onChange={onChange}
              className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
            >
              <option value="">Select Chorionicity</option>
              <option value="Monochorionic">Monochorionic</option>
              <option value="Dichorionic">Dichorionic</option>
              <option value="Unknown">Unknown</option>
            </select>
          </div>
        </>
      )}
    </div>
  );

  const renderMeasurements = () => (
    <div className="space-y-4">
      <div>
        <label className="block mb-1">Weight Unit</label>
        <div className="flex gap-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="unit"
              value="US"
              checked={weightUnit.unit === "US"}
              onChange={handleWeightUnitChange}
              className="mr-2"
            />
            US (lbs/oz)
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="unit"
              value="Metric"
              checked={weightUnit.unit === "Metric"}
              onChange={handleWeightUnitChange}
              className="mr-2"
            />
            Metric (kg)
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {weightUnit.unit === "US" ? (
          <>
            <div>
              <label className="block mb-1">Weight (lbs)</label>
              <input
                name="weightLbs"
                type="number"
                min="0"
                max="20"
                step="0.1"
                value={baby.weightLbs || ""}
                onChange={onChange}
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                placeholder="e.g. 7"
              />
            </div>
            <div>
              <label className="block mb-1">Weight (oz)</label>
              <input
                name="weightOz"
                type="number"
                min="0"
                max="15.9"
                step="0.1"
                value={baby.weightOz || ""}
                onChange={onChange}
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                placeholder="e.g. 8"
              />
            </div>
          </>
        ) : (
          <div>
            <label className="block mb-1">Weight (kg)</label>
            <input
              name="weightKg"
              type="number"
              min="0"
              max="10"
              step="0.01"
              value={baby.weightKg || ""}
              onChange={onChange}
              className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              placeholder="e.g. 3.5"
            />
          </div>
        )}

        <div>
          <label className="block mb-1">Birth Length (cm)</label>
          <input
            name="birthLength"
            type="number"
            min="20"
            max="70"
            step="0.1"
            value={baby.birthLength || ""}
            onChange={onChange}
            className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
            placeholder="e.g. 50"
          />
        </div>
      </div>
    </div>
  );

  const renderLaborStages = () => {
    const totalDuration = calculateTotalDuration(baby);

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4">
          <LaborStageRecorder
            label="Early Labor"
            startName="earlyLaborStart"
            value={baby}
            onChange={onChange}
            required={true}
          />
          <LaborStageRecorder
            label="Active Labor"
            startName="activeLaborStart"
            value={baby}
            onChange={onChange}
            previousStageStart="earlyLaborStart"
            required={true}
          />
          <LaborStageRecorder
            label="Second Stage"
            startName="secondStageStart"
            value={baby}
            onChange={onChange}
            previousStageStart="activeLaborStart"
            required={true}
          />
          <LaborStageRecorder
            label="Third Stage"
            startName="thirdStageStart"
            value={baby}
            onChange={onChange}
            previousStageStart="secondStageStart"
            required={true}
          />
          <LaborStageRecorder
            label="Placenta Delivery"
            startName="placentaDeliveryTime"
            value={baby}
            onChange={onChange}
            previousStageStart="thirdStageStart"
            required={true}
          />
        </div>

        {totalDuration && (
          <div className="border rounded-lg p-4 bg-white">
            <h3 className="font-medium text-emerald-700 mb-2">Total Labor Duration</h3>
            <div className="text-lg">
              {totalDuration.hours}h {totalDuration.minutes}m
              {!baby.placentaDeliveryTime && " (ongoing)"}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBirthDetails = () => {
    const timeHeadOut = calculateTimeDifference(baby.headBornTime, baby.bodyBornTime);
    const timeToFirstBreath = calculateTimeDifference(baby.headBornTime, baby.firstBreathTime);

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Left Column - Critical Times */}
          <div className="space-y-4">
            <div className="border rounded-lg p-4 bg-white">
              <h3 className="font-medium text-emerald-700 mb-3">Critical Times</h3>
              <div className="space-y-3">
                <TimeRecordButton
                  label="Head Born At"
                  time={baby.headBornTime}
                  fieldName="headBornTime"
                  onChange={onChange}
                />
                <TimeRecordButton
                  label="Body Born At"
                  time={baby.bodyBornTime}
                  fieldName="bodyBornTime"
                  onChange={onChange}
                />
                <TimeRecordButton
                  label="First Breath"
                  time={baby.firstBreathTime}
                  fieldName="firstBreathTime"
                  onChange={onChange}
                />
                <div>
                  <label className="block mb-1">Time Head Out</label>
                  <div className="px-3 py-2 bg-gray-100 rounded text-gray-700">
                    {timeHeadOut || "Waiting for times..."}
                  </div>
                </div>
                <div>
                  <label className="block mb-1">Time to First Breath</label>
                  <div className="px-3 py-2 bg-gray-100 rounded text-gray-700">
                    {timeToFirstBreath || "Waiting for times..."}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Middle Column - Birth Details */}
          <div className="space-y-4">
            <div className="border rounded-lg p-4 bg-white">
              <h3 className="font-medium text-emerald-700 mb-3">Birth Details</h3>
              <div className="space-y-3">
                <div>
                  <label className="block mb-1">Presentation*</label>
                  <select
                    name="presentation"
                    value={baby.presentation || ""}
                    onChange={onChange}
                    required
                    className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                  >
                    <option value="">Select Presentation</option>
                    <option value="LOA">LOA</option>
                    <option value="ROA">ROA</option>
                    <option value="OP">OP</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-1">Birth Position*</label>
                  <select
                    name="birthPosition"
                    value={baby.birthPosition || ""}
                    onChange={onChange}
                    required
                    className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                  >
                    <option value="">Select Position</option>
                    <option value="Birth Stool">Birth Stool</option>
                    <option value="Hands & Knees">Hands & Knees</option>
                    <option value="Water">Water</option>
                    <option value="Lithotomy">Lithotomy</option>
                    <option value="Side-Lying">Side-Lying</option>
                    <option value="Squatting">Squatting</option>
                    <option value="Standing">Standing</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-1">Method of Birth*</label>
                  <select
                    name="birthMethod"
                    value={baby.birthMethod || ""}
                    onChange={onChange}
                    required
                    className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                  >
                    <option value="">Select Method</option>
                    <option value="NSVD">NSVD</option>
                    <option value="VBAC">VBAC</option>
                    <option value="Forceps">Forceps</option>
                    <option value="Vacuum">Vacuum</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-1">Method Notes</label>
                  <input
                    type="text"
                    name="methodNotes"
                    value={baby.methodNotes || ""}
                    onChange={onChange}
                    className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                    placeholder="Additional notes..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Additional Details */}
          <div className="space-y-4">
            <div className="border rounded-lg p-4 bg-white">
              <h3 className="font-medium text-emerald-700 mb-3">Additional Details</h3>
              <div className="space-y-3">
                <div>
                  <label className="block mb-1">Nuchal Cord*</label>
                  <select
                    name="nuchalCord"
                    value={baby.nuchalCord || ""}
                    onChange={onChange}
                    required
                    className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                  >
                    <option value="">Select</option>
                    <option value="None">None</option>
                    <option value="Once">Once</option>
                    <option value="Twice">Twice</option>
                    <option value="Multiple">Multiple</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-1">Place of Birth*</label>
                  <select
                    name="birthPlace"
                    value={baby.birthPlace || ""}
                    onChange={onChange}
                    required
                    className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                  >
                    <option value="">Select Place</option>
                    <option value="Home">Home</option>
                    <option value="Birth Center">Birth Center</option>
                    <option value="Hospital">Hospital</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-1">Suction*</label>
                  <select
                    name="suction"
                    value={baby.suction || ""}
                    onChange={onChange}
                    required
                    className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                  >
                    <option value="">Select Type</option>
                    <option value="None">None</option>
                    <option value="Bulb">Bulb</option>
                    <option value="DeLee">DeLee</option>
                    <option value="Mechanical">Mechanical</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Water Usage Section */}
            <div className="border rounded-lg p-4 bg-white">
              <h3 className="font-medium text-emerald-700 mb-3">Water Usage</h3>
              <div className="space-y-3">
                <RadioGroup
                  label="Water for Pain Relief"
                  name="waterPainRelief"
                  value={baby.waterPainRelief}
                  onChange={onChange}
                />
                <RadioGroup
                  label="Water in Active 1st Stage"
                  name="waterFirstStage"
                  value={baby.waterFirstStage}
                  onChange={onChange}
                />
                <RadioGroup
                  label="Water in 2nd Stage"
                  name="waterSecondStage"
                  value={baby.waterSecondStage}
                  onChange={onChange}
                />
                <RadioGroup
                  label="Baby Born in Water"
                  name="waterBirth"
                  value={baby.waterBirth}
                  onChange={onChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row - APGAR and Resuscitation in 2 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* APGAR Scores */}
          <div className="border rounded-lg p-4 bg-white">
            <h3 className="font-medium text-emerald-700 mb-3">APGAR Scores</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block mb-1">1 Min*</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      name="apgar1Min"
                      value={baby.apgar1Min || ""}
                      onChange={onChange}
                      min="0"
                      max="10"
                      required
                      className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                    />
                    <button
                      type="button"
                      onClick={() => handleOpenAPGARDialog(1)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-2 rounded text-sm"
                    >
                      Score
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block mb-1">5 Min*</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      name="apgar5Min"
                      value={baby.apgar5Min || ""}
                      onChange={onChange}
                      min="0"
                      max="10"
                      required
                      className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                    />
                    <button
                      type="button"
                      onClick={() => handleOpenAPGARDialog(5)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-2 rounded text-sm"
                    >
                      Score
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block mb-1">10 Min</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      name="apgar10Min"
                      value={baby.apgar10Min || ""}
                      onChange={onChange}
                      min="0"
                      max="10"
                      className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                    />
                    <button
                      type="button"
                      onClick={() => handleOpenAPGARDialog(10)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-2 rounded text-sm"
                    >
                      Score
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Resuscitation Measures */}
          <div className="border rounded-lg p-4 bg-white">
            <h3 className="font-medium text-emerald-700 mb-3">Resuscitation Measures</h3>
            <div className="grid grid-cols-3 gap-4">
              <RadioGroup
                label="PPV"
                name="ppv"
                value={baby.ppv}
                onChange={onChange}
              />
              <RadioGroup
                label="Oxygen"
                name="oxygen"
                value={baby.oxygen}
                onChange={onChange}
              />
              <RadioGroup
                label="Chest Compressions"
                name="chestCompressions"
                value={baby.chestCompressions}
                onChange={onChange}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleSave} className="bg-white rounded-xl shadow text-sm">
      {/* Status Messages */}
      {showStatus && saveStatus === "saved" && (
        <div className="text-green-700 bg-green-100 border border-green-300 rounded-t-xl px-3 py-2 flex justify-between items-center">
          <span>Birth event saved successfully.</span>
          <button
            type="button"
            onClick={() => setShowStatus(false)}
            className="text-green-700 hover:text-green-900"
          >
            ×
          </button>
        </div>
      )}
      {showStatus && saveStatus === "error" && (
        <div className="text-red-700 bg-red-100 border border-red-300 rounded-t-xl px-3 py-2 flex justify-between items-center">
          <span>Error saving birth event. Please try again.</span>
          <button
            type="button"
            onClick={() => setShowStatus(false)}
            className="text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </div>
      )}

      {/* Twin Status Banner */}
      {baby.isTwin && (
        <div className="bg-emerald-100 border border-emerald-300 px-3 py-2 flex items-center">
          <span className="text-emerald-700 font-medium">
            Twin {baby.twinOrder} Birth Event
          </span>
        </div>
      )}

      <div className="flex">
        {/* Main Form Content */}
        <div className="flex-grow border-r">
          {/* Tabs */}
          <div className="border-b">
            <nav className="flex gap-2 px-4 flex-wrap">
              <TabButton
                isActive={activeTab === "basic"}
                onClick={() => setActiveTab("basic")}
              >
                Basic Info
              </TabButton>
              <TabButton
                isActive={activeTab === "measurements"}
                onClick={() => setActiveTab("measurements")}
              >
                Measurements
              </TabButton>
              <TabButton
                isActive={activeTab === "labor"}
                onClick={() => setActiveTab("labor")}
              >
                Labor Stages
              </TabButton>
              <TabButton
                isActive={activeTab === "birth"}
                onClick={() => setActiveTab("birth")}
              >
                Birth Details
              </TabButton>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-4">
            {activeTab === "basic" && renderBasicInfo()}
            {activeTab === "measurements" && renderMeasurements()}
            {activeTab === "labor" && renderLaborStages()}
            {activeTab === "birth" && renderBirthDetails()}
          </div>
        </div>

        {/* Persistent Labor Notes Panel */}
        <div className="w-1/4 min-w-[300px] p-4">
          <div className="sticky top-4 flex flex-col h-[calc(100vh-250px)]">
            <h3 className="font-medium text-emerald-700 mb-3">Labor Notes</h3>
            
            {/* Note Input Area */}
            <div className="mb-2">
              <textarea
                value={currentNote}
                onChange={(e) => setCurrentNote(e.target.value)}
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full h-32 resize-none"
                placeholder={`Record observations, interventions, and notable events...${
                  baby.isTwin ? '\n\nInclude any twin-specific observations for Twin ' + baby.twinOrder : ''
                }`}
              />
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={handleAddNote}
                  disabled={!currentNote.trim()}
                  className={`px-3 py-1 rounded text-white text-sm ${
                    currentNote.trim()
                      ? 'bg-emerald-600 hover:bg-emerald-700'
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                >
                  Add Note
                </button>
              </div>
            </div>

            {/* Notes Display Area */}
            <div className="flex-1 overflow-y-auto border rounded-lg bg-gray-50">
              <div className="p-3 space-y-3">
                {(baby.laborNotes || []).length === 0 ? (
                  <p className="text-gray-500 text-sm italic text-center">
                    No notes recorded yet
                  </p>
                ) : (
                  (baby.laborNotes || []).map((note, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg shadow-sm p-3 border"
                    >
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-xs text-gray-500">
                          {new Date(note.timestamp).toLocaleString()}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            const updatedNotes = (baby.laborNotes || []).filter((_, i) => i !== index);
                            onChange({
                              target: {
                                name: "laborNotes",
                                value: updatedNotes
                              }
                            });
                          }}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.content}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end p-4 bg-gray-50 rounded-b-xl border-t">
        <button
          type="submit"
          className={`px-4 py-2 rounded text-white text-sm font-medium ${
            saveStatus === "saving"
              ? "bg-yellow-400"
              : saveStatus === "error"
              ? "bg-red-500"
              : "bg-emerald-600 hover:bg-emerald-700"
          }`}
          disabled={saveStatus === "saving"}
        >
          {saveStatus === "saving"
            ? "Saving..."
            : saveStatus === "error"
            ? "Retry Save"
            : `Save ${baby.isTwin ? `Twin ${baby.twinOrder}` : 'Birth'} Event`}
        </button>
      </div>

      {/* APGAR Scoring Dialog */}
      <APGARScoringDialog
        isOpen={showAPGARDialog}
        onClose={() => setShowAPGARDialog(false)}
        onScoreSubmit={handleAPGARScoreSubmit}
        timePoint={currentAPGARTimepoint}
      />

      {/* Breach Birth Details - Only show if birth type is Breach */}
      {baby.birthType === 'Breach' && (
        <div className="bg-white p-4 rounded-xl shadow mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Breach Birth Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block mb-1">Breach Type*</label>
              <select
                name="breachType"
                value={baby.breachType || ""}
                onChange={onChange}
                required
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              >
                <option value="">Select Type</option>
                <option value="Frank">Frank (Complete)</option>
                <option value="Complete">Complete (Flexed)</option>
                <option value="Footling">Footling</option>
                <option value="Kneeling">Kneeling</option>
              </select>
            </div>
            <div>
              <label className="block mb-1">Breach Extraction Method*</label>
              <select
                name="breachExtractionMethod"
                value={baby.breachExtractionMethod || ""}
                onChange={onChange}
                required
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              >
                <option value="">Select Method</option>
                <option value="Spontaneous">Spontaneous</option>
                <option value="Assisted">Assisted</option>
                <option value="Total">Total Extraction</option>
              </select>
            </div>
            <div>
              <label className="block mb-1">Head Delivery Method*</label>
              <select
                name="breachHeadDelivery"
                value={baby.breachHeadDelivery || ""}
                onChange={onChange}
                required
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              >
                <option value="">Select Method</option>
                <option value="Spontaneous">Spontaneous</option>
                <option value="MSV">Mauriceau-Smellie-Veit</option>
                <option value="Forceps">Forceps</option>
              </select>
            </div>
            <div>
              <label className="block mb-1">Complications</label>
              <select
                name="breachComplications"
                value={baby.breachComplications || ""}
                onChange={onChange}
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              >
                <option value="">Select Complication</option>
                <option value="None">None</option>
                <option value="ExtendedArms">Extended Arms</option>
                <option value="ExtendedHead">Extended Head</option>
                <option value="NuchalArms">Nuchal Arms</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block mb-1">Additional Notes</label>
              <textarea
                name="breachNotes"
                value={baby.breachNotes || ""}
                onChange={onChange}
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                rows={3}
                placeholder="Enter any additional notes about the breach birth..."
              />
            </div>
          </div>
        </div>
      )}
    </form>
  );
}